import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import CircularProgressWithLabel from '~components/CircularProgressWithLabel';
import EmptyState from '~components/EmptyState';
import ControlledFileUpload from '~components/Form/ControlledFileUpload';
import OberonCard from '~components/OberonCard';
import OberonDialog from '~components/OberonDialog';
import { UploadValidationFailure } from '~hooks/usePublicHoliday/api';
import { useNotification } from '~providers/NotificationProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';

interface ImportPublicHolidayModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File, onUploadProgress: (progress: number) => void) => Promise<{ inserted: number; total: number }>;
}

interface UploadResult {
  inserted: number;
  total: number;
}

interface ErrorDisplay {
  text: string;
  subText: string;
}

interface Form {
  file: File | null;
}

interface UploadProgress {
  isUploading: boolean;
  uploadProgress: number;
}

const EXAMPLE_HEADERS = ['local_date', 'timezone', 'name', 'information', 'postcode'];
const EXAMPLE_ROWS: Record<string, string>[] = [
  {
    local_date: "'2024-01-01",
    timezone: 'Australia/Sydney',
    name: "New Year's Day",
    information: 'Public holiday',
    postcode: '2000',
  },
  {
    local_date: "'2024-04-25",
    timezone: 'Australia/Melbourne',
    name: 'Anzac Day',
    information: 'Day of remembrance',
    postcode: '',
  },
  {
    local_date: "'2024-03-29",
    timezone: 'Australia/Brisbane',
    name: 'Good Friday',
    information: '',
    postcode: '4000',
  },
  {
    local_date: "'2024-12-25",
    timezone: 'Australia/Perth',
    name: 'Christmas Day',
    information: '',
    postcode: '',
  },
];

const ImportPublicHolidayModal = ({ open, onClose, onUpload }: ImportPublicHolidayModalProps) => {
  const { pushNotification } = useNotification();
  const [uploadResult, setUploadResult] = useState<UploadResult | undefined>(undefined);
  const [validationFailures, setValidationFailures] = useState<UploadValidationFailure[]>([]);
  const [uploadError, setUploadError] = useState<ErrorDisplay | undefined>(undefined);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    isUploading: false,
    uploadProgress: 0,
  });

  const {
    formState: { errors, isValid },
    handleSubmit,
    control,
    reset,
    watch,
  } = useForm<Form>({
    defaultValues: {
      file: null,
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });

  useEffect(() => {
    return function cleanup() {
      reset();
      setUploadResult(undefined);
      setValidationFailures([]);
      setUploadError(undefined);
      setSubmitting(false);
      setUploadProgress({ isUploading: false, uploadProgress: 0 });
    };
  }, [open]);

  // Clear any previous upload result the moment the user picks a new file
  const selectedFile = watch('file');
  useEffect(() => {
    setUploadResult(undefined);
    setValidationFailures([]);
    setUploadError(undefined);
  }, [selectedFile]);

  const handleProgress = (progress: number) => {
    setUploadProgress((prev) => ({ ...prev, uploadProgress: progress }));
  };

  const handleDownloadExample = () => {
    const csvLines = [
      EXAMPLE_HEADERS.join(','),
      ...EXAMPLE_ROWS.map((r) => EXAMPLE_HEADERS.map((h) => r[h]).join(',')),
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'public_holidays_example.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const uploadFile = handleSubmit(async (data: Form) => {
    const file = data.file as File;

    setSubmitting(true);
    setUploadError(undefined);
    setUploadResult(undefined);
    setValidationFailures([]);
    setUploadProgress({ isUploading: true, uploadProgress: 0 });

    try {
      const result = await onUpload(file, handleProgress);
      setUploadResult(result);
      pushNotification('success', `Import complete - inserted ${result.inserted} public holidays`);
    } catch (e) {
      if (e instanceof APIError) {
        const failures = (e as any).failures as UploadValidationFailure[] | undefined;
        if (failures && failures.length > 0) {
          setValidationFailures(failures);
        } else {
          setUploadError({ text: 'Unable to import public holidays', subText: e.message });
        }
      } else if (e instanceof UnsupportedStructureError) {
        setUploadError({ text: 'Data from backend invalid', subText: 'Unable to decode response' });
      } else {
        setUploadError({ text: 'An unexpected error occurred', subText: 'Please try again' });
      }
    } finally {
      setUploadProgress({ isUploading: false, uploadProgress: 0 });
      setSubmitting(false);
    }
  });

  const errorResultDisplay = validationFailures.map((failure, index) => {
    const id = `${index}-row-${failure.row}`;
    return (
      <OberonCard
        key={id}
        reverseTitles
        titleFontWeight={700}
        title={failure.reason}
        subHeader={`Row ${failure.row} - ${failure.field}`}
      />
    );
  });

  const isComplete = uploadResult !== undefined;

  return (
    <OberonDialog
      title='Import Public Holiday'
      open={open}
      onSubmit={uploadFile}
      content={
        <Grid
          container
          spacing={1}
          sx={{
            justifyContent: 'center',
            alignContent: 'center',
          }}>
          {/* File picker and upload button */}
          <Grid
            size={{
              xs: 12,
              md: 8,
            }}>
            <ControlledFileUpload
              control={control}
              rules={{ required: 'File is required.' }}
              name='file'
              label='File (.csv or .xlsx)'
              disabled={submitting || isComplete}
              required={true}
              error={Boolean(errors.file)}
              helperText={errors.file?.message}
            />
          </Grid>

          <Grid
            style={{ display: 'flex', alignItems: 'center' }}
            size={{
              xs: 12,
              md: 4,
            }}>
            <LoadingButton
              fullWidth
              type='submit'
              variant='contained'
              disableElevation
              startIcon={<CloudUploadIcon />}
              disabled={!isValid || submitting || isComplete}
              loading={submitting}
              color='primary'
              sx={{ height: '56px' }}>
              Upload
            </LoadingButton>
          </Grid>

          {/* Instructional note (hidden after successful import) */}
          {!isComplete && (
            <Grid size={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                }}>
                <InfoOutlinedIcon color='info' />
                <Typography variant='h6' component='h2'>
                  How it works
                </Typography>
              </Box>
              <List dense disablePadding sx={{ pl: 4, mb: 2 }}>
                <ListItem disableGutters disablePadding sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText primary='File format: CSV or XLSX' sx={{ my: 0 }} />
                </ListItem>
                <ListItem disableGutters disablePadding sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText
                    primary='Required columns: local_date, timezone, name, information, postcode'
                    sx={{ my: 0 }}
                  />
                </ListItem>
                <ListItem disableGutters disablePadding sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText
                    primary='Required values: local_date, timezone, name (information and postcode can be empty)'
                    sx={{ my: 0 }}
                  />
                </ListItem>
                <ListItem disableGutters disablePadding sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText primary='Date format: YYYY-MM-DD' sx={{ my: 0 }} />
                </ListItem>
                <ListItem disableGutters disablePadding sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText primary='All rows must be from the same year' sx={{ my: 0 }} />
                </ListItem>
                <ListItem disableGutters disablePadding sx={{ display: 'list-item', listStyleType: 'disc' }}>
                  <ListItemText
                    primary={`If editing in Excel, prefix dates with ' (e.g. '2024-01-01) to prevent auto-conversion`}
                    sx={{ my: 0 }}
                  />
                </ListItem>
              </List>
              <Button variant='outlined' size='small' onClick={handleDownloadExample} sx={{ mb: 2 }}>
                Download example CSV
              </Button>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2,
                }}>
                <WarningAmberIcon color='warning' />
                <Typography variant='body1'>Uploading replaces all existing public holidays for that year.</Typography>
              </Box>
            </Grid>
          )}

          {/* Upload error (non-validation) */}
          {uploadError && (
            <Grid size={12}>
              <EmptyState type='error' text={uploadError.text} subText={uploadError.subText} />
            </Grid>
          )}

          {!uploadError && (
            <>
              {/* Upload progress */}
              {uploadProgress.isUploading && (
                <Grid style={{ marginTop: 40, textAlign: 'center' }} size={12}>
                  <CircularProgressWithLabel value={uploadProgress.uploadProgress} />
                  <Typography variant='body1' component='p' color='textSecondary'>
                    Uploading file...
                  </Typography>
                </Grid>
              )}

              {/* Success result */}
              {isComplete && (
                <Grid style={{ marginTop: 16 }} size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}>
                    <CheckCircleIcon color='success' />
                    <Typography variant='h6' component='h2'>
                      {uploadResult.inserted} row{uploadResult.inserted > 1 ? 's' : ''} successfully uploaded
                    </Typography>
                  </Box>
                  <Typography variant='body1' component='p'>
                    You can now close the modal.
                  </Typography>
                </Grid>
              )}

              {/* Validation failures */}
              {validationFailures.length > 0 && (
                <Grid style={{ marginTop: 16 }} size={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}>
                    <CancelIcon color='error' />
                    <Typography variant='h6' component='h2'>
                      {validationFailures.length} row{validationFailures.length > 1 ? 's' : ''} failed validation
                    </Typography>
                  </Box>

                  <Typography variant='body1' component='p' sx={{ marginBottom: 2 }}>
                    Fix the issues below and re-upload the file.
                  </Typography>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>{errorResultDisplay}</Box>
                </Grid>
              )}
            </>
          )}
        </Grid>
      }
      actionFooter={
        <>
          <Button onClick={onClose}>{isComplete ? 'Close' : 'Cancel'}</Button>
        </>
      }
    />
  );
};

export default ImportPublicHolidayModal;
